<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import MultiSelect from '@shared/components/MultiSelect/MultiSelect.vue';
    import Modal from '@shared/components/Modal.vue';
    import SelectItemFromList from '@shared/components/SelectItemFromList.vue';
    import { ItemTypes } from '@shared/components/SelectItemFromList';
    import FormTextArea from '@shared/components/Form/FormTextArea.vue';
    import FeatureLock from '@shared/components/FeatureLock.vue';
    import InfoPopoverButton from '@shared/components/Form/InfoPopoverButton.vue';
    import { setupTags } from '@shared/components/Tags/Tags';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import { formatNumericText } from '@shared/utils/formatNumericText';
    import { splitTextAroundLink } from '@shared/utils/formatText';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { TaskStatus } from '@views/BackOffice/HealthManager/HealthManager.types';
    import { type List, type WelcomeIssueReference, type WelcomeSmsReference, type ListPropertyValue } from './ListsTypes.d';
    import { updateListProperty, loadIssuesForWelcomeEmail, loadSmsForWelcomeSms } from './Lists.access';
    import { type Tag } from '@shared/components/Tags/TagsTypes';
    import type { Issue } from '@views/Issues/interfaces';
    import type { TextMessage } from '@views/TextMessages/TextMessagesTypes.d';

    const props = defineProps<{ listId: string; list: List | null; importStarted: boolean; canWrite: boolean }>();

    const name = ref('');
    const description = ref('');
    const savedName = ref('');
    const savedDescription = ref('');
    const selectedTags = ref<Tag[]>([]);
    const welcomeIssue = ref<WelcomeIssueReference | null>(null);
    const showWelcomeIssueModal = ref(false);
    const welcomeIssues = ref<Issue[]>([]);
    const welcomeIssuesLoaded = ref(false);

    const welcomeSms = ref<WelcomeSmsReference | null>(null);
    const showWelcomeSmsModal = ref(false);
    const welcomeSmsMessages = ref<TextMessage[]>([]);
    const welcomeSmsLoaded = ref(false);

    const tags = setupTags();

    watch(
        () => props.list,
        (list) => {
            if (!list) return;
            name.value = list.Name;
            description.value = list.Description ?? '';
            savedName.value = name.value;
            savedDescription.value = description.value;
            selectedTags.value = list.Tags ?? [];
            welcomeIssue.value = list.WelcomeIssue ?? null;
            welcomeSms.value = list.WelcomeSmsMessage ?? null;
        },
        { immediate: true }
    );

    const scheduledImportsParts = computed(() => {
        if (!props.list) return null;
        return splitTextAroundLink(
            $Resources.ListHasScheduledImportsLabel.replace('{{Count}}', props.list.ScheduledImportsCount.toString()).replace(
                '{{Url}}',
                '/Automation/Tasks/' + TaskStatus.Scheduled + '/' + props.list.ListId
            )
        );
    });

    const failedImportsParts = computed(() => {
        if (!props.list) return null;
        return splitTextAroundLink(
            $Resources.ListHasFailedImportsLabel.replace('{{Count}}', props.list.FailedImportsCount.toString()).replace(
                '{{Url}}',
                '/Automation/Tasks/' + TaskStatus.Failed + '/' + props.list.ListId
            )
        );
    });

    async function openWelcomeIssueModal() {
        if (!props.canWrite) return;
        if (!welcomeIssuesLoaded.value) {
            welcomeIssues.value = await loadIssuesForWelcomeEmail();
            welcomeIssuesLoaded.value = true;
        }
        showWelcomeIssueModal.value = true;
    }

    async function selectWelcomeIssue(issueId: string) {
        const issue = welcomeIssues.value.find((i) => i.IssueId === issueId);
        if (!issue) return;

        welcomeIssue.value = { IssueId: issue.IssueId, Subject: issue.Subject, IssueName: issue.IssueName, Status: String(issue.Status) };
        showWelcomeIssueModal.value = false;

        saveListProperty('WelcomeIssue', { issueId: issue.IssueId });
    }

    async function clearWelcomeIssue() {
        welcomeIssue.value = null;
        saveListProperty('WelcomeIssue', { issueId: null });
    }

    async function openWelcomeSmsModal() {
        if (!props.canWrite) return;
        if (!welcomeSmsLoaded.value) {
            welcomeSmsMessages.value = await loadSmsForWelcomeSms();
            welcomeSmsLoaded.value = true;
        }
        showWelcomeSmsModal.value = true;
    }

    async function selectWelcomeSms(smsId: string) {
        const sms = welcomeSmsMessages.value.find((s) => s.SmsMessageId === smsId);
        if (!sms) return;

        welcomeSms.value = { SmsMessageId: sms.SmsMessageId, Subject: sms.Subject };
        showWelcomeSmsModal.value = false;

        saveListProperty('WelcomeSmsMessage', { SmsMessageId: sms.SmsMessageId });
    }

    async function clearWelcomeSms() {
        welcomeSms.value = null;
        saveListProperty('WelcomeSmsMessage', { SmsMessageId: null });
    }

    async function saveListProperty(property: string, value: ListPropertyValue) {
        // Single funnel point for every list-property write below (Name/Description/Tags/Welcome
        // Issue/Welcome SMS) -- the backend now rejects these outright for a Read-only caller, this
        // just stops the request from being sent in the first place, matching the disabled form
        // controls below (defense in depth, same as the other editors' write guards).
        if (!props.canWrite) return;

        if (property === 'Name' && (!props.list || name.value === savedName.value)) {
            return;
        }

        if (property === 'Description' && (!props.list || description.value === savedDescription.value)) {
            return;
        }

        try {
            await updateListProperty(props.listId, property, value);
            if (property === 'Name') savedName.value = value as string;
            if (property === 'Description') savedDescription.value = value as string;
            showAlertAfterUpdate('success', property);
        } catch {
            showAlertAfterUpdate('error', property);
        }
    }

    function showAlertAfterUpdate(type: 'success' | 'error', property: string) {
        const content = $Resources[`PropertyList${property}Label`];

        if (type === 'success') {
            bsAlert({ title: $Resources.PropertyUpdatedLabel, content: content });
        } else if (type === 'error') {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.PropertyNotUpdatedLabel, content: content });
        }
    }

    async function onTagsUpdated(newTags: Tag[]) {
        selectedTags.value = newTags;

        saveListProperty(
            'Tags',
            newTags.map((t) => ({ TagId: t.TagId }))
        );
    }

    async function onNewTag(title: string) {
        await tags.create(title, selectedTags.value);
        await onTagsUpdated(selectedTags.value);
    }
</script>

<template>
    <div v-if="list" class="list-header p-0-b b-0-b">
        <FormTextArea
            v-model="name"
            class="form-group form-group-h2"
            :label="$Resources.ListNameLabel"
            :placeholder="$Resources.ListNamePlaceholder"
            :disableNewlines="true"
            :disabled="!canWrite"
            :maxlength="255"
            @onBlur="saveListProperty('Name', name)" />

        <div class="row">
            <div class="col-md-3">
                <FormTextArea
                    class="form-group"
                    v-model="description"
                    :label="$Resources.ListDescriptionLabel"
                    :placeholder="$Resources.ListDescriptionPlaceholder"
                    :disableNewlines="true"
                    :disabled="!canWrite"
                    @onBlur="saveListProperty('Description', description)" />
            </div>

            <div class="col-md-3">
                <MultiSelect
                    class="form-group"
                    :label="$Resources.TagsLabel"
                    :infoText="$Resources.TagsHelpText"
                    :items="tags.state.list"
                    :selectedItems="selectedTags"
                    selectProp="TagId"
                    displayProp="Title"
                    :placeholder="$Resources.TagsPlaceholder"
                    :disabled="!canWrite"
                    @update:selectedItems="onTagsUpdated"
                    @newItem="onNewTag" />
            </div>

            <div class="col-md-3">
                <FeatureLock feature="Email.ListWelcomeEmail" :hideWhenLocked="false">
                    <div class="m-lg-b">
                        <span class="label-look">
                            {{ $Resources.WelcomeIssueLabel }}:
                            <InfoPopoverButton :heading="$Resources.WelcomeIssueLabel" :info-text="$Resources.WelcomeIssueHelpText" direction="right" />
                        </span>
                        <div class="d-flex align-items-center gap-xs justify-content-between">
                            <a v-if="welcomeIssue" :href="`/Issues/${welcomeIssue.IssueId}/Statistics`" target="_blank" rel="noopener">
                                {{ welcomeIssue.Subject }}
                            </a>
                            <span v-else class="text-placeholder" :class="{ 'pe-none o-60': !canWrite }" @click="openWelcomeIssueModal">
                                {{ $Resources.ChooseWelcomeIssuePlaceholder }}
                            </span>
                            <button v-if="welcomeIssue" class="btn btn-subtle" type="button" :disabled="!canWrite" @click="clearWelcomeIssue">
                                {{ $Resources.RemoveWelcomeIssueButton }}
                            </button>
                            <button v-else class="btn btn-subtle" type="button" :disabled="!canWrite" @click="openWelcomeIssueModal">
                                {{ $Resources.ChooseWelcomeIssueButton }}
                            </button>
                        </div>
                    </div>
                </FeatureLock>
            </div>

            <div class="col-md-3">
                <FeatureLock feature="Sms.ListWelcomeSms" :hideWhenLocked="false">
                    <div class="m-lg-b">
                        <span class="label-look">
                            {{ $Resources.WelcomeSmsMessageLabel }}:
                            <InfoPopoverButton
                                :heading="$Resources.WelcomeSmsMessageLabel"
                                :info-text="$Resources.WelcomeSmsMessageHelpText"
                                direction="right" />
                        </span>
                        <div class="d-flex align-items-center gap-xs justify-content-between">
                            <a v-if="welcomeSms" :href="`/TextMessages/${welcomeSms.SmsMessageId}/Statistics`" target="_blank" rel="noopener">
                                {{ welcomeSms.Subject }}
                            </a>
                            <span v-else class="text-placeholder" :class="{ 'pe-none o-60': !canWrite }" @click="openWelcomeSmsModal">
                                {{ $Resources.ChooseWelcomeSmsMessagePlaceholder }}
                            </span>
                            <button v-if="welcomeSms" class="btn btn-subtle" type="button" :disabled="!canWrite" @click="clearWelcomeSms">
                                {{ $Resources.RemoveWelcomeSmsMessageButton }}
                            </button>
                            <button v-else class="btn btn-subtle" type="button" :disabled="!canWrite" @click="openWelcomeSmsModal">
                                {{ $Resources.ChooseWelcomeSmsMessageButton }}
                            </button>
                        </div>
                    </div>
                </FeatureLock>
            </div>
        </div>

        <p class="meta">
            <span class="glyphicon glyphicon-time"></span>
            {{ $Resources.GenericCreatedLabel }}
            {{ formatDateTime(list.Created) }}
            <template v-if="list.CreatedBy">{{ $Resources.GenericByLabel }} {{ list.CreatedBy.Name }}</template>

            <i class="fa fa-pencil" aria-hidden="true"></i>
            {{ $Resources.LastModifiedLabel }}
            {{ formatDateTime(list.LastModified) }}
            <template v-if="list.ModifiedBy">{{ $Resources.GenericByLabel }} {{ list.ModifiedBy.Name }}</template>

            <span class="glyphicon glyphicon-user"></span>
            {{ formatNumericText(list.ContactCount, { numberWithSpace: true }) }}

            <template v-if="list.ScheduledImportsCount > 0 && scheduledImportsParts">
                <span class="glyphicon glyphicon-circle-arrow-down m-xs-r c-primary"></span>
                {{ scheduledImportsParts.before }}
                <router-link :to="scheduledImportsParts.linkHref">{{ scheduledImportsParts.linkText }}</router-link>
                {{ scheduledImportsParts.after }}
            </template>

            <template v-if="list.FailedImportsCount > 0 && failedImportsParts">
                <span class="glyphicon glyphicon-ban-circle m-xs-r c-danger"></span>
                {{ failedImportsParts.before }}
                <router-link :to="failedImportsParts.linkHref">{{ failedImportsParts.linkText }}</router-link>
                {{ failedImportsParts.after }}
            </template>
        </p>

        <div v-if="list.ActiveImportsCount > 0 || importStarted" class="text-center">
            <p>{{ $Resources.ImportActiveLabel }}</p>
            <span class="busy-working is-busy m-xs-t" aria-hidden="true"></span>
        </div>
    </div>

    <Modal :title="$Resources.ChooseIssueHeading" :show="showWelcomeIssueModal" @close="showWelcomeIssueModal = false" size="lg">
        <template #content>
            <BsAlert
                :type="AlertTypes.Info"
                :dismissible="false"
                :title="$Resources.IssueNeedsToBeSentAlertHeading"
                :content="$Resources.IssueNeedsToBeSentAlertText" />
            <SelectItemFromList :items="welcomeIssues" :type="ItemTypes.Issues" @update:selectedId="(id: string) => selectWelcomeIssue(id)" />
        </template>
    </Modal>

    <Modal :title="$Resources.ChooseTextMessageHeading" :show="showWelcomeSmsModal" @close="showWelcomeSmsModal = false" size="lg">
        <template #content>
            <BsAlert
                :type="AlertTypes.Info"
                :dismissible="false"
                :title="$Resources.IssueNeedsToBeSentAlertHeading"
                :content="$Resources.IssueNeedsToBeSentAlertText" />
            <SelectItemFromList :items="welcomeSmsMessages" :type="ItemTypes.Sms" @update:selectedId="(id: string) => selectWelcomeSms(id)" />
        </template>
    </Modal>
</template>
